/**
 * Auction Event Model for the component
 * This is the interface used throughout the application
 */

export interface TranslatableString {
  en: string;
  ar: string;
  es?: string;
  [key: string]: string | undefined;
}

export interface AuctionEvent {
  id: number;
  title: TranslatableString;
  date: Date;
  time: string;
  location: TranslatableString;
  description: TranslatableString;
  status: 'upcoming' | 'active' | 'completed';
  auctionUrl?: string | null;
  consignor?: TranslatableString | null;
  email?: string | null;
  phone?: string | null;
  // New date fields from API
  previewDateStart?: string | null;
  previewDateEnd?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  // Legacy field for backward compatibility (computed from previewDateStart/End)
  previewDate?: string;
  // Interest tracking
  isInterested?: boolean;
}

/**
 * Extract translated text from a translatable field
 * Falls back to English if requested language not found
 */
export function getTranslation(
  field: TranslatableString | string | undefined,
  currentLang: string = 'en'
): string {
  // Handle undefined/null
  if (!field) return '';

  // Handle plain string (backward compatibility)
  if (typeof field === 'string') return field;

  // Try current language
  const currentLangValue = field[currentLang];
  if (currentLangValue) {
    return currentLangValue;
  }

  // Fallback to English
  const enValue = field['en'];
  if (enValue) {
    return enValue;
  }

  // Last resort: return first available translation
  const firstValue = Object.values(field).find(v => v);
  return firstValue || '';
}
